import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAccessibility } from '../contexts/AccessibilityContext';
import { useConferenceYear } from '../contexts/ConferenceYearContext';
import { dbHelpers } from '../lib/supabase';
import { fetchSessionById, fetchSessionByNumber } from '../services/supabaseService';
import Loading from '../components/Loading';
import PresenterUrlSubmission from '../components/PresenterUrlSubmission';
import PosterPresentation from '../components/PosterPresentation';
import AacButton from '../components/aac/AacButton';

import { trackBookmarkAdd, trackBookmarkRemove, trackAgendaAdd, trackAgendaRemove } from '../utils/analytics';
import { useFocusableNavigation } from '../hooks/useFocusableNavigation';

const SessionDetail = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { announce, focusFirstHeading } = useAccessibility();
  useFocusableNavigation();
  const { activeYear } = useConferenceYear();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [showPoster, setShowPoster] = useState(false);

  const loadSessionDetail = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // A session_number looks like "1.1", "P5", etc.; a bare numeric string
      // is the session's database ID.
      const isSessionNumber = sessionId.includes('.') || isNaN(sessionId);

      const sessionData = isSessionNumber
        ? await fetchSessionByNumber(sessionId, activeYear)
        : await fetchSessionById(parseInt(sessionId, 10));

      if (!sessionData) {
        throw new Error('Session not found');
      }

      setSession(sessionData);
      announce(`Loaded session details for ${sessionData.title}`);
    } catch (err) {
      console.error('Error loading session:', err);
      setError(err.message);
      announce('Error loading session details');
    } finally {
      setLoading(false);
    }
  }, [sessionId, announce, activeYear]);

  useEffect(() => {
    loadSessionDetail();
  }, [sessionId, loadSessionDetail]);

  useEffect(() => {
    if (session && !loading) {
      focusFirstHeading();
    }
  }, [session, loading, focusFirstHeading]);

  useEffect(() => {
    if (!session) return;

    const checkBookmarkStatus = async () => {
      try {
        const { data: bookmarks, error: bookmarkError } = await dbHelpers.getUserBookmarks();

        if (bookmarkError) {
          console.error('Error checking bookmark status:', bookmarkError);
          setIsBookmarked(false);
          return;
        }

        const bookmarksArray = Array.isArray(bookmarks) ? bookmarks : [];
        setIsBookmarked(bookmarksArray.includes(session.id));
      } catch (checkError) {
        console.error('Error checking bookmark status:', checkError);
        setIsBookmarked(false);
      }
    };

    checkBookmarkStatus();
  }, [session]);

  const toggleBookmark = async () => {
    if (!session) return;
    const sessionTitle = session.title || 'Session';

    try {
      if (isBookmarked) {
        const result = await dbHelpers.removeBookmark(session.id);

        if (result.success) {
          setIsBookmarked(false);
          trackBookmarkRemove(session.id, 'session');
          trackAgendaRemove(session.id, sessionTitle);
          announce(`${sessionTitle} removed from your agenda`);
        } else {
          console.error('Failed to remove bookmark:', result.message);
        }
      } else {
        const result = await dbHelpers.addBookmark(session.id);

        if (result.success) {
          setIsBookmarked(true);
          trackBookmarkAdd(session.id, 'session');
          trackAgendaAdd(session.id, sessionTitle);
          announce(`${sessionTitle} added to your agenda`);
        } else {
          console.error('Failed to add bookmark:', result.message);
        }
      }
    } catch (toggleError) {
      console.error('Error toggling bookmark:', toggleError);
      announce('Error updating bookmark. Please try again.');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Date TBA';

    try {
      const date = dateString.match(/^\d{4}-\d{2}-\d{2}$/)
        ? new Date(`${dateString}T00:00:00`)
        : new Date(dateString);

      if (isNaN(date.getTime())) {
        return 'Date TBA';
      }

      return date.toLocaleDateString('en-GB', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (formatError) {
      console.error('Error formatting date:', formatError);
      return 'Date TBA';
    }
  };

  const handleBackToProgramme = () => {
    navigate('/programme');
  };

  if (loading) {
    return (
      <div className="session-detail-page">
        <div className="container">
          <Loading message="Loading session details..." />
        </div>
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="session-detail-page">
        <div className="container">
          <div className="error-state">
            <h1>Session Not Found</h1>
            <p>Sorry, we couldn&apos;t find the session you&apos;re looking for.</p>
            <div className="error-actions">
              <button onClick={handleBackToProgramme} className="btn btn-primary">
                ← Back to Programme
              </button>
              <button
                onClick={() => navigate(-1)}
                className="btn btn-outline"
              >
                Go Back
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Keynote sessions store their rich write-up in `description`; every other
  // session's content lives in `abstract` (the submitted abstract).
  const isKeynote = session.track === 'Keynote' || session.title?.toLowerCase().includes('keynote');
  const descriptionContent = isKeynote
    ? (session.description || session.abstract)
    : (session.abstract || session.description);

  return (
    <div className="session-detail-page">
      <div className="container">
        {/* Breadcrumb Navigation */}
        <nav aria-label="Breadcrumb" className="breadcrumb">
          <ol className="breadcrumb-list">
            <li className="breadcrumb-item">
              <Link to="/">Home</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/programme">Programme</Link>
            </li>
            <li className="breadcrumb-item" aria-current="page">
              {session.title}
            </li>
          </ol>
        </nav>

        {/* Session Header */}
        <header className="session-header">
          <div className="session-meta">
            <div className="session-badges">
              {session.category && (
                <span className="session-category-badge">{session.category}</span>
              )}
              {session.track === 'Lightning Talks' && (
                <span className="session-track">⚡ Lightning Talks</span>
              )}
            </div>

            <h1 className={`session-title ${session.status === 'cancelled' ? 'cancelled-session' : ''}`}>
              {session.title}
              {session.status === 'cancelled' && <span className="cancelled-indicator"> (CANCELLED)</span>}
            </h1>
            {session.status === 'cancelled' && (
              <span className="status-badge status-cancelled">Cancelled</span>
            )}
          </div>

          <div className="session-details">
            <div className="session-time-info">
              <time dateTime={session.date || undefined}>
                <span className="session-date">📅 {formatDate(session.date)}</span>
                <span className="session-time">🕐 {session.time || 'Time TBA'}</span>
              </time>
              {/* `location` is usually just a room code ("LG10") — combine it
                  with the building name so delegates know which building to
                  head to, not just which room once they're already there. */}
              {(session.building || session.location || session.room) && (
                <div className="session-location">
                  📍 {[session.building, session.location || session.room].filter(Boolean).join(', ')}
                </div>
              )}

              {session.sessionNumber && (
                <div className="session-number-info">
                  📋 Session: {session.sessionNumber}
                </div>
              )}
            </div>

            <div className="session-speakers">
              <h2 className="speakers-heading">Speaker(s)</h2>
              <div className="speakers-list">
                {session.presenters && session.presenters.length > 0 ? (
                  session.presenters.map((presenter, index) => (
                    <span key={`presenter-${index}`} className="speaker-name">
                      {presenter}
                      {index < session.presenters.length - 1 && ', '}
                    </span>
                  ))
                ) : (
                  <span className="speaker-name">Speaker TBA</span>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Session Content */}
        <div className="session-content">
          {session.status === 'cancelled' && session.cancellation_notes && (
            <section className="cancellation-notice">
              <h2>Cancellation Notice</h2>
              <p className="cancellation-notes">{session.cancellation_notes}</p>
            </section>
          )}

          <section className="session-description">
            <h2>Session Description</h2>
            <div className="description-content">
              {descriptionContent
                ? descriptionContent.split('\n').map((paragraph, index) => (
                  <p key={`abstract-paragraph-${index}`}>{paragraph}</p>
                ))
                : <p>Description will be updated soon.</p>}
            </div>
          </section>
        </div>

        {/* Action Buttons */}
        <div className="session-actions">
          <AacButton
            onClick={toggleBookmark}
            variant={isBookmarked ? 'secondary' : 'primary'}
            shortcut={1}
            aria-pressed={isBookmarked}
            title={isBookmarked ? 'Remove from agenda' : 'Add to agenda'}
          >
            <span aria-hidden="true">{isBookmarked ? '⭐' : '☆'}</span>
            {isBookmarked ? 'Remove from Agenda' : 'Add to Agenda'}
          </AacButton>

          {session.posterImageUrl && (
            <AacButton
              onClick={() => setShowPoster(true)}
              variant="secondary"
              shortcut={3}
              title="View poster"
            >
              <span aria-hidden="true">🖼️</span> View Poster
            </AacButton>
          )}

          <AacButton
            onClick={handleBackToProgramme}
            variant="secondary"
            shortcut={2}
            title="Back to Programme"
          >
            ← Back to Programme
          </AacButton>

          <button
            onClick={() => navigate(-1)}
            className="btn btn-outline"
          >
            Go Back
          </button>
        </div>

        {/* Presenter URL Submission Section */}
        <PresenterUrlSubmission
          sessionId={session.id}
          sessionTitle={session.title || 'Session'}
        />

        {showPoster && session.posterImageUrl && (
          <PosterPresentation
            posters={[session]}
            startIndex={0}
            onClose={() => setShowPoster(false)}
          />
        )}
      </div>
    </div>
  );
};

export default SessionDetail;
